import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";

import {
	authApi,
	buyerPreferencesApi,
	uiApi,
	userPromptSettingsApi,
} from "../../../../shared/api";
import { USER_PROMPT_MAX_LENGTH } from "../../../../shared/constants/default-user-prompt";
import type { RootState } from "../../../../shared/store/store";
import { clearClientAccessibleCookies } from "../../../../shared/utils/clearClientAccessibleCookies";
import { hasChatModules } from "../../../../shared/utils/has-chat-modules";

export type HeaderReportInfo = {
	file_name: string | null;
	file_size: number | null;
	last_modified: string | null;
};

export function useHeaderModals() {
	const [isDocsOpen, setIsDocsOpen] = useState(false);
	const [docsHtml, setDocsHtml] = useState<string>("");
	const [isDocsLoading, setIsDocsLoading] = useState(false);
	const [docsError, setDocsError] = useState<string | null>(null);

	const [isReportOpen, setIsReportOpen] = useState(false);
	const [isReportLoading, setIsReportLoading] = useState(false);
	const [reportInfo, setReportInfo] = useState<HeaderReportInfo | null>(null);
	const [reportError, setReportError] = useState<string | null>(null);

	const [isAuthErrorOpen, setIsAuthErrorOpen] = useState(false);

	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [isSettingsLoading, setIsSettingsLoading] = useState(false);
	const [isSettingsApplying, setIsSettingsApplying] = useState(false);
	const [buyerPreferencesLoaded, setBuyerPreferencesLoaded] = useState(false);
	const [settingsError, setSettingsError] = useState<string | null>(null);
	const [autoAcceptApps, setAutoAcceptApps] = useState<boolean>(false);
	const [savedAutoAcceptApps, setSavedAutoAcceptApps] =
		useState<boolean>(false);
	const [autoOverwriteCert, setAutoOverwriteCert] = useState<boolean>(true);
	const [savedAutoOverwriteCert, setSavedAutoOverwriteCert] =
		useState<boolean>(true);
	const [settingsLastUpdatedAt, setSettingsLastUpdatedAt] = useState<
		string | null
	>(null);
	const [userPromptDraft, setUserPromptDraft] = useState("");
	const [savedUserPrompt, setSavedUserPrompt] = useState<string | null>(null);
	const [userPromptLoaded, setUserPromptLoaded] = useState(false);
	const [userPromptError, setUserPromptError] = useState<string | null>(null);

	const isChatConnected = useSelector((state: RootState) =>
		hasChatModules(state.user?.modules),
	);

	const openAuthError = useCallback(() => setIsAuthErrorOpen(true), []);

	const formatFileSize = useCallback((bytes: number) => {
		if (!Number.isFinite(bytes) || bytes < 0) return "—";
		if (bytes === 0) return "0 Б";
		const k = 1024;
		const sizes = ["Б", "КБ", "МБ", "ГБ"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
	}, []);

	const formatDateTime = useCallback((iso: string) => {
		try {
			const d = new Date(iso);
			if (Number.isNaN(d.getTime())) return iso;
			return d.toLocaleString("uk-UA", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});
		} catch {
			return iso;
		}
	}, []);

	const docsSafeHtml = useMemo(() => {
		if (!docsHtml) return "";
		return DOMPurify.sanitize(docsHtml);
	}, [docsHtml]);

	const openDocs = useCallback(async () => {
		setIsDocsOpen(true);
		setDocsError(null);
		setDocsHtml("");
		setIsDocsLoading(true);

		// Fire-and-forget analytics (do not block UI)
		void uiApi.logDocsClick({
			timestamp: new Date().toISOString(),
			page: window.location.pathname,
		});

		const res = await uiApi.getDocs();
		if (!res.success) {
			if ((res.message || "").toLowerCase().includes("401")) {
				openAuthError();
			}
			setDocsError(res.message || "Документація недоступна.");
			setIsDocsLoading(false);
			return;
		}

		setDocsHtml(res.data?.content ?? "");
		setIsDocsLoading(false);
	}, [openAuthError]);

	const closeDocs = useCallback(() => setIsDocsOpen(false), []);

	const openReport = useCallback(async () => {
		setIsReportOpen(true);
		setReportError(null);
		setReportInfo(null);
		setIsReportLoading(true);

		const res = await uiApi.getReportInfo();
		if (!res.success) {
			if ((res.message || "").toLowerCase().includes("401")) {
				setIsReportOpen(false);
				openAuthError();
				setIsReportLoading(false);
				return;
			}
			setReportError(res.message || "Не вдалося отримати інформацію про звіт.");
			setIsReportLoading(false);
			return;
		}

		setReportInfo(res.data ?? null);
		setIsReportLoading(false);
	}, [openAuthError]);

	const closeReport = useCallback(() => setIsReportOpen(false), []);

	const downloadReport = useCallback(async () => {
		setReportError(null);
		const res = await uiApi.downloadReport();
		if (!res.success || !res.data?.download_url) {
			setReportError(res.message || "Не вдалося завантажити звіт.");
			return;
		}

		const link = document.createElement("a");
		link.href = res.data.download_url;
		link.download = res.data.filename || "report";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setIsReportOpen(false);
	}, []);

	const openSettings = useCallback(async () => {
		setIsSettingsOpen(true);
		setSettingsError(null);
		setUserPromptError(null);
		setBuyerPreferencesLoaded(false);
		setUserPromptLoaded(false);
		setIsSettingsLoading(true);

		const [prefsRes, promptRes] = await Promise.all([
			buyerPreferencesApi.getBuyerPreferences(),
			isChatConnected
				? userPromptSettingsApi.getUserPromptSettings()
				: Promise.resolve(null),
		]);

		if (!prefsRes.success) {
			if ((prefsRes.message || "").toLowerCase().includes("401")) {
				setIsSettingsOpen(false);
				openAuthError();
				setIsSettingsLoading(false);
				return;
			}
			setSettingsError(
				prefsRes.message || "Не вдалося завантажити налаштування.",
			);
			setIsSettingsLoading(false);
			return;
		}

		const nextAuto = Boolean(prefsRes.data?.auto_accept_apps);
		setAutoAcceptApps(nextAuto);
		setSavedAutoAcceptApps(nextAuto);
		// Backend default is true (DAI-1240); treat missing field as enabled.
		const nextOverwrite =
			typeof prefsRes.data?.auto_overwrite_cert === "boolean"
				? prefsRes.data.auto_overwrite_cert
				: true;
		setAutoOverwriteCert(nextOverwrite);
		setSavedAutoOverwriteCert(nextOverwrite);
		setBuyerPreferencesLoaded(true);
		const fromApi = prefsRes.data?.last_updated_at;
		setSettingsLastUpdatedAt(
			typeof fromApi === "string" && fromApi.length > 0
				? fromApi
				: new Date().toISOString(),
		);

		if (promptRes) {
			if (!promptRes.success) {
				if ((promptRes.message || "").toLowerCase().includes("401")) {
					setIsSettingsOpen(false);
					openAuthError();
					setIsSettingsLoading(false);
					return;
				}
				setUserPromptError(
					promptRes.message || "Не вдалося завантажити промпт текстогенерації.",
				);
				setUserPromptDraft("");
				setSavedUserPrompt(null);
				setUserPromptLoaded(false);
			} else {
				const stored =
					typeof promptRes.data?.prompt_text === "string"
						? promptRes.data.prompt_text
						: null;
				setSavedUserPrompt(stored);
				setUserPromptDraft(stored ?? "");
				setUserPromptLoaded(true);
			}
		}

		setIsSettingsLoading(false);
	}, [isChatConnected, openAuthError]);

	const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

	const setAutoAcceptAppsDraft = useCallback((next: boolean) => {
		setAutoAcceptApps(next);
	}, []);

	const setAutoOverwriteCertDraft = useCallback((next: boolean) => {
		setAutoOverwriteCert(next);
	}, []);

	const setUserPromptDraftValue = useCallback((next: string) => {
		setUserPromptDraft(next);
		setUserPromptError(null);
	}, []);

	const savedPromptText = savedUserPrompt ?? "";
	const prefsDirty =
		autoAcceptApps !== savedAutoAcceptApps ||
		autoOverwriteCert !== savedAutoOverwriteCert;
	const promptDirty =
		isChatConnected &&
		userPromptLoaded &&
		userPromptDraft !== savedPromptText;
	// Empty / whitespace draft is allowed: BJ PUT clears the active prompt.
	const promptInvalid =
		isChatConnected &&
		userPromptLoaded &&
		userPromptDraft.length > USER_PROMPT_MAX_LENGTH;

	const applySettings = useCallback(async () => {
		if (
			!buyerPreferencesLoaded ||
			(!prefsDirty && !promptDirty) ||
			promptInvalid
		) {
			return;
		}

		setIsSettingsApplying(true);
		setSettingsError(null);
		setUserPromptError(null);
		let prefsOk = !prefsDirty;
		let promptOk = !promptDirty;

		try {
			if (prefsDirty) {
				const body: {
					auto_accept_apps?: boolean;
					auto_overwrite_cert?: boolean;
				} = {};
				if (autoAcceptApps !== savedAutoAcceptApps) {
					body.auto_accept_apps = autoAcceptApps;
				}
				if (autoOverwriteCert !== savedAutoOverwriteCert) {
					body.auto_overwrite_cert = autoOverwriteCert;
				}
				const res = await buyerPreferencesApi.updateBuyerPreferences(body);
				if (!res.success) {
					if ((res.message || "").toLowerCase().includes("401")) {
						setIsSettingsOpen(false);
						openAuthError();
						return;
					}
					setSettingsError(res.message || "Не вдалося зберегти налаштування.");
				} else {
					prefsOk = true;
					setSavedAutoAcceptApps(autoAcceptApps);
					setSavedAutoOverwriteCert(autoOverwriteCert);
					const fromApi = res.data?.last_updated_at;
					setSettingsLastUpdatedAt(
						typeof fromApi === "string" && fromApi.length > 0
							? fromApi
							: new Date().toISOString(),
					);
				}
			}

			if (promptDirty) {
				const res = await userPromptSettingsApi.updateUserPromptSettings({
					prompt_text: userPromptDraft,
				});
				if (!res.success) {
					if ((res.message || "").toLowerCase().includes("401")) {
						setIsSettingsOpen(false);
						openAuthError();
						return;
					}
					setUserPromptError(
						res.message || "Не вдалося зберегти промпт текстогенерації.",
					);
				} else {
					promptOk = true;
					// Clear path: BJ returns prompt_text null → empty UI state.
					const stored =
						typeof res.data?.prompt_text === "string"
							? res.data.prompt_text
							: null;
					setSavedUserPrompt(stored);
					setUserPromptDraft(stored ?? "");
				}
			}

			if (prefsOk && promptOk) {
				setIsSettingsOpen(false);
			}
		} finally {
			setIsSettingsApplying(false);
		}
	}, [
		autoAcceptApps,
		autoOverwriteCert,
		buyerPreferencesLoaded,
		openAuthError,
		prefsDirty,
		promptDirty,
		promptInvalid,
		savedAutoAcceptApps,
		savedAutoOverwriteCert,
		userPromptDraft,
	]);

	const isSettingsApplyDisabled =
		!buyerPreferencesLoaded ||
		isSettingsLoading ||
		isSettingsApplying ||
		(!prefsDirty && !promptDirty) ||
		promptInvalid;

	const closeAuthError = useCallback(() => setIsAuthErrorOpen(false), []);

	const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
	const [isLogoutLoading, setIsLogoutLoading] = useState(false);
	const [logoutError, setLogoutError] = useState<string | null>(null);

	const openLogoutConfirm = useCallback(() => {
		setLogoutError(null);
		setIsLogoutConfirmOpen(true);
	}, []);
	const closeLogoutConfirm = useCallback(
		() => setIsLogoutConfirmOpen(false),
		[],
	);

	const confirmLogout = useCallback(async () => {
		setIsLogoutLoading(true);
		setLogoutError(null);

		await authApi.logout();

		try {
			// Удаляем конфигурацию блоков из localStorage
			localStorage.removeItem("modulesConfig");
			localStorage.removeItem("sectionsConfig");
		} catch {
			// ignore
		}

		clearClientAccessibleCookies();

		setIsLogoutLoading(false);
		setIsLogoutConfirmOpen(false);
		window.location.href = "/auth/login";
	}, []);

	return {
		// state
		isDocsOpen,
		isDocsLoading,
		docsError,
		docsSafeHtml,

		isReportOpen,
		isReportLoading,
		reportInfo,
		reportError,

		isSettingsOpen,
		isSettingsLoading,
		isSettingsApplying,
		isSettingsApplyDisabled,
		buyerPreferencesLoaded,
		settingsError,
		autoAcceptApps,
		autoOverwriteCert,
		settingsLastUpdatedAt,
		isChatConnected,
		userPromptDraft,
		userPromptError,

		isAuthErrorOpen,
		isLogoutConfirmOpen,
		isLogoutLoading,
		logoutError,

		// helpers
		formatFileSize,
		formatDateTime,

		// actions
		openDocs,
		closeDocs,
		openReport,
		closeReport,
		downloadReport,
		openSettings,
		closeSettings,
		setAutoAcceptAppsDraft,
		setAutoOverwriteCertDraft,
		setUserPromptDraftValue,
		applySettings,
		closeAuthError,
		openLogoutConfirm,
		closeLogoutConfirm,
		confirmLogout,
	};
}
